import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { Empty, Tooltip, Popover, Input, Checkbox, Button } from 'antd';
import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  LeftOutlined,
  RightOutlined,
  DownloadOutlined,
  FilterOutlined
} from '@ant-design/icons';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getPaginationRowModel,
  flexRender
} from '@tanstack/react-table';
import * as XLSX from 'xlsx';
import Spinner from '../../CustomComponents/Spinner';
import { createColumns } from './tanstackColumnConfig';
import { getAriaSortValue } from './columnHelpers';
import * as Styled from './TanStackTableStyles';

const ITEMS_PER_PAGE = 50;

/**
 * Renders a searchable checkbox list of unique column values for filtering.
 * Defined outside TanStackJobTable so it can use useState without hook-order issues.
 */
const FilterPopoverContent = ({ column }) => {
  const [search, setSearch] = useState('');
  const currentFilter = column.getFilterValue() ?? [];
  const facetedMap = column.getFacetedUniqueValues();

  const options = Array.from(facetedMap.entries())
    .map(([value, count]) => ({
      label: value != null ? String(value) : 'N/A',
      count
    }))
    .filter(({ label }) =>
      label.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div style={{ minWidth: 180, maxWidth: 260, padding: '0 8px' }}>
      <Input
        size='small'
        placeholder='Search...'
        value={search}
        onChange={e => setSearch(e.target.value)}
        onClick={e => e.stopPropagation()}
        style={{ marginBottom: 8 }}
        allowClear
      />
      <div style={{ maxHeight: 200, overflowY: 'auto' }}>
        {options.length === 0 ? (
          <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12, padding: '4px 0' }}>
            No options
          </div>
        ) : (
          options.map(({ label, count }) => (
            <div key={label} style={{ padding: '3px 0' }}>
              <Checkbox
                checked={currentFilter.includes(label)}
                onChange={e => {
                  const newValues = e.target.checked
                    ? [...currentFilter, label]
                    : currentFilter.filter(v => v !== label);
                  column.setFilterValue(newValues.length > 0 ? newValues : undefined);
                }}
              >
                <span style={{ fontSize: 13 }}>{label}</span>
              </Checkbox>
            </div>
          ))
        )}
      </div>
      {currentFilter.length > 0 && (
        <div style={{ borderTop: '1px solid #f0f2f5', marginTop: 8, paddingTop: 6, textAlign: 'right' }}>
          <Button
            type='link'
            size='small'
            onClick={() => column.setFilterValue(undefined)}
          >
            Clear
          </Button>
        </div>
      )}
    </div>
  );
};

const TanStackJobTable = ({
  // Data props
  availableJobs,
  tableLoading,

  // Search
  searchValue,

  // Filters
  filterConfig,

  // Sorting props
  filterKey,
  sortOrder,
  setFilterKey,
  setSortOrder,

  // Tab state
  activeKey,
  activeTab1,
  type,

  // Column customization props
  visibleColumns,
  setVisibleColumns,
  columnWidths,
  setColumnWidths,

  // Clear filters trigger
  clearFiltersTrigger,

  // Export callback
  onExportReady,

  // Action handlers
  startAssignedJobWithNewEditor,
  resumeJobWithNewEditor,
  viewJob,
  releaseJobHandler,
  disableUser,
  selectedId,
  assignButtonLoading,
  user,
  jobStatus,
  setJobStatus,
  tempStatus,
  setTempStatus
}) => {
  const [theme] = useSelector(state => [state.themeReducer]);

  // Single session-storage key per tab, stores { sort, filters }
  const storageKey = `job_list_${activeKey}_${activeTab1 || ''}`;

  const loadPersisted = key => {
    try {
      return JSON.parse(sessionStorage.getItem(key)) || {};
    } catch {
      return {};
    }
  };

  // Local state for TanStack Table — initialized from persisted blob
  const persisted = useRef(loadPersisted(storageKey)).current;
  const [sortingState, setSortingState] = useState(
    persisted.sort ?? [{ id: 'submissionTime', desc: true }]
  );
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState(persisted.filters ?? []);
  const [filterVisible, setFilterVisible] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnSizing, setColumnSizing] = useState({});
  const [rowSelection, setRowSelection] = useState({});

  // Column ordering state
  const [columnOrder, setColumnOrder] = useState([]);
  const [draggedColumnId, setDraggedColumnId] = useState(null);
  const [dragOverColumnId, setDragOverColumnId] = useState(null);
  const NON_DRAGGABLE_COLUMNS = ['delete', 'release'];

  // Initialize column visibility from props
  useEffect(() => {
    setColumnVisibility(visibleColumns);
  }, [visibleColumns]);

  // Initialize column sizing from props
  useEffect(() => {
    setColumnSizing(columnWidths);
  }, [columnWidths]);

  // Column drag-and-drop handlers
  const handleColumnDragStart = useCallback((e, headerId) => {
    setDraggedColumnId(headerId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleColumnDragOver = useCallback((e, headerId) => {
    e.preventDefault();
    if (headerId !== draggedColumnId && !NON_DRAGGABLE_COLUMNS.includes(headerId)) {
      e.dataTransfer.dropEffect = 'move';
      setDragOverColumnId(headerId);
    }
  }, [draggedColumnId]);

  const handleColumnDragLeave = useCallback(() => {
    setDragOverColumnId(null);
  }, []);

  const handleColumnDrop = useCallback((e, targetId) => {
    e.preventDefault();
    if (!draggedColumnId || draggedColumnId === targetId || NON_DRAGGABLE_COLUMNS.includes(targetId)) {
      setDraggedColumnId(null);
      setDragOverColumnId(null);
      return;
    }
    setColumnOrder(prev => {
      const newOrder = [...prev];
      const fromIdx = newOrder.indexOf(draggedColumnId);
      const toIdx = newOrder.indexOf(targetId);
      newOrder.splice(fromIdx, 1);
      newOrder.splice(toIdx, 0, draggedColumnId);
      return newOrder;
    });
    setDraggedColumnId(null);
    setDragOverColumnId(null);
  }, [draggedColumnId]);

  const handleColumnDragEnd = useCallback(() => {
    setDraggedColumnId(null);
    setDragOverColumnId(null);
  }, []);

  // Note: Sorting is handled entirely client-side by TanStack Table
  // No need to sync with parent sorting state

  // Sync search value with global filter
  useEffect(() => {
    setGlobalFilter(searchValue || '');
  }, [searchValue]);

  // Reload persisted state when tab changes (switching tabs mid-session)
  useEffect(() => {
    const p = loadPersisted(storageKey);
    setSortingState(p.sort ?? [{ id: 'submissionTime', desc: true }]);
    setColumnFilters(p.filters ?? []);
  }, [storageKey]);

  // Persist sort + filters together whenever either changes
  useEffect(() => {
    const current = loadPersisted(storageKey);
    sessionStorage.setItem(storageKey, JSON.stringify({
      ...current,
      sort: sortingState,
      filters: columnFilters,
    }));
  }, [sortingState, columnFilters, storageKey]);

  // Clear all column filters when parent signals
  useEffect(() => {
    if (clearFiltersTrigger) {
      setColumnFilters([]);
    }
  }, [clearFiltersTrigger]);

  // Custom filter function for facility and author filters
  const customFilterFn = useMemo(() => {
    return row => {
      const job = row.original;

      // Apply facility filter
      if (filterConfig?.facilityIds && filterConfig.facilityIds.length > 0) {
        const facilityId = job.facility?.facilityId || job.facility?.id;
        if (!filterConfig.facilityIds.includes(facilityId)) {
          return false;
        }
      }

      // Apply doctor/author filter
      if (filterConfig?.doctorIds && filterConfig.doctorIds.length > 0) {
        const authorId = job.authorId || job.author?.id;
        if (!filterConfig.doctorIds.includes(authorId)) {
          return false;
        }
      }

      return true;
    };
  }, [filterConfig]);

  // Filter data based on custom filters
  const filteredData = useMemo(() => {
    if (!availableJobs) return [];

    // Apply custom filters (facility, author)
    if (
      filterConfig &&
      (filterConfig.facilityIds?.length > 0 ||
        filterConfig.doctorIds?.length > 0)
    ) {
      return availableJobs.filter(job => {
        // Check facility filter
        if (filterConfig.facilityIds && filterConfig.facilityIds.length > 0) {
          const facilityId = job.facility?.facilityId || job.facility?.id;
          if (!filterConfig.facilityIds.includes(facilityId)) {
            return false;
          }
        }

        // Check doctor/author filter
        if (filterConfig.doctorIds && filterConfig.doctorIds.length > 0) {
          const authorId = job.authorId || job.author?.id;
          if (!filterConfig.doctorIds.includes(authorId)) {
            return false;
          }
        }

        return true;
      });
    }

    return availableJobs;
  }, [availableJobs, filterConfig]);

  // Handle sorting change (client-side only, no API calls)
  const handleSortingChange = updater => {
    const newSorting =
      typeof updater === 'function' ? updater(sortingState) : updater;
    setSortingState(newSorting);
    // Note: Sorting is handled entirely client-side by TanStack Table
    // No need to sync to parent state or trigger API calls
  };

  // Handle column visibility change
  const handleColumnVisibilityChange = updater => {
    const newVisibility =
      typeof updater === 'function' ? updater(columnVisibility) : updater;
    setColumnVisibility(newVisibility);
    setVisibleColumns(newVisibility);
  };

  // Handle column sizing change
  const handleColumnSizingChange = updater => {
    const newSizing =
      typeof updater === 'function' ? updater(columnSizing) : updater;
    setColumnSizing(newSizing);

    // Sync to parent's columnWidths
    if (Object.keys(newSizing).length > 0) {
      setColumnWidths(newSizing);
    }
  };

  // Memoized column definitions
  const columns = useMemo(
    () =>
      createColumns({
        activeKey,
        activeTab1,
        startAssignedJobWithNewEditor,
        resumeJobWithNewEditor,
        viewJob,
        releaseJobHandler,
        disableUser
      }),
    [
      activeKey,
      activeTab1,
      startAssignedJobWithNewEditor,
      resumeJobWithNewEditor,
      viewJob,
      releaseJobHandler,
      disableUser
    ]
  );

  // Initialize column order when columns change (e.g., tab switch)
  useEffect(() => {
    setColumnOrder(columns.map(col => col.id));
  }, [columns]);

  // TanStack Table instance
  const table = useReactTable({
    data: filteredData,
    columns,

    // Client-side pagination
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: ITEMS_PER_PAGE,
        pageIndex: 0
      }
    },

    // Client-side sorting
    getSortedRowModel: getSortedRowModel(),
    manualSorting: false,
    enableMultiSort: true,

    // Client-side filtering
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: 'includesString',

    // Column features
    enableColumnResizing: true,
    columnResizeMode: 'onChange',

    // State
    state: {
      sorting: sortingState,
      globalFilter,
      columnFilters,
      columnVisibility,
      columnSizing,
      columnOrder,
      rowSelection
    },

    // Callbacks
    onSortingChange: handleSortingChange,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onColumnSizingChange: handleColumnSizingChange,
    onColumnOrderChange: setColumnOrder,
    onRowSelectionChange: setRowSelection,

    // Core models
    getCoreRowModel: getCoreRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),

    // Row identification
    getRowId: row => row.jobId,

    // Row selection (only for incomplete tab)
    enableRowSelection: type === 'incomplete'
  });

  // Excel export function
  const exportToExcel = useCallback(() => {
    const visibleRows = table.getRowModel().rows;
    const visibleColumnIds = table
      .getVisibleLeafColumns()
      .filter(
        col => col.id !== 'delete' && col.id !== 'release'
      )
      .map(col => col.id);

    const exportData = visibleRows.map(row => {
      const rowData = {};
      visibleColumnIds.forEach(colId => {
        const cell = row.getAllCells().find(c => c.column.id === colId);
        const column = table.getColumn(colId);
        if (cell && column) {
          // Get raw value for export
          rowData[column.columnDef.header] = cell.getValue();
        }
      });
      return rowData;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Jobs');

    const fileName = `jobs_${activeKey}_${
      new Date().toISOString().split('T')[0]
    }.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }, [table, activeKey]);

  // Expose export function to parent
  useEffect(() => {
    if (onExportReady) {
      onExportReady(exportToExcel);
    }
  }, [exportToExcel, onExportReady]);

  // Empty state check
  const isEmpty = !availableJobs || availableJobs.length === 0;

  const tableContainerRef = useRef(null);
  const topScrollRef = useRef(null);
  const tableRef = useRef(null);

  useEffect(() => {
    const container = tableContainerRef.current;
    const tableEl = tableRef.current;
    if (!container) return;
    let rafId;
    const updateWidth = () => {
      rafId = requestAnimationFrame(() => {
        if (topScrollRef.current?.firstChild) {
          topScrollRef.current.firstChild.style.width = `${container.scrollWidth}px`;
        }
      });
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    if (tableEl) observer.observe(tableEl);
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [availableJobs, columnSizing, columnVisibility, columnOrder]);

  const handleTopScroll = () => {
    if (tableContainerRef.current && topScrollRef.current) {
      tableContainerRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  };

  const handleBottomScroll = () => {
    if (topScrollRef.current && tableContainerRef.current) {
      topScrollRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Styled.TableContainer
        ref={tableContainerRef}
        onScroll={handleBottomScroll}
        style={{ position: 'relative' }}
      >
        {/* Top scrollbar mirror */}
        <Styled.TopScrollbar ref={topScrollRef} onScroll={handleTopScroll}>
          <div style={{ height: 1 }} />
        </Styled.TopScrollbar>

        {/* Loading overlay */}
        {tableLoading && (
          <Styled.LoadingOverlay>
            <Spinner />
          </Styled.LoadingOverlay>
        )}

        {/* Empty state */}
        {isEmpty && !tableLoading ? (
          <Styled.EmptyState>
            <Empty description='No jobs found' />
          </Styled.EmptyState>
        ) : (
          <>
            {/* Export button positioned above table */}
            {/* <div style={{ padding: '10px', textAlign: 'right', background: '#fff', borderBottom: '1px solid #f0f2f5' }}>
              <Styled.ExportButton
                icon={<DownloadOutlined />}
                onClick={exportToExcel}
              >
                Export to Excel
              </Styled.ExportButton>
            </div> */}

            {/* Table */}
            <Styled.StyledTable
              ref={tableRef}
              role='table'
              aria-label='Job list table'
              style={{
                width: table.getTotalSize(),
                minWidth: '100%',
                tableLayout: 'fixed',
                transition: table.getState().columnSizingInfo.isResizingColumn
                  ? 'none'
                  : 'width 0.15s ease'
              }}
            >
              {/* Table Head */}
              <Styled.StyledTableHead role='rowgroup'>
                {table.getHeaderGroups().map(headerGroup => (
                  <Styled.StyledTableHeaderRow key={headerGroup.id} role='row'>
                    {headerGroup.headers.map(header => {
                      const isDraggable = !NON_DRAGGABLE_COLUMNS.includes(header.id);
                      return (
                        <Styled.DraggableHeader
                          key={header.id}
                          role='columnheader'
                          aria-sort={getAriaSortValue(
                            header.column.getIsSorted()
                          )}
                          style={{ width: header.getSize() }}
                          sortable={header.column.getCanSort()}
                          sorted={header.column.getIsSorted()}
                          isDragging={draggedColumnId === header.id}
                          isOver={dragOverColumnId === header.id}
                          onDragOver={isDraggable ? e => handleColumnDragOver(e, header.id) : undefined}
                          onDragLeave={isDraggable ? handleColumnDragLeave : undefined}
                          onDrop={isDraggable ? e => handleColumnDrop(e, header.id) : undefined}
                        >
                          <div
                            draggable={isDraggable}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: isDraggable ? 'grab' : 'default' }}
                            onClick={
                              header.column.getCanSort() && !draggedColumnId
                                ? header.column.getToggleSortingHandler()
                                : undefined
                            }
                            tabIndex={header.column.getCanSort() ? 0 : -1}
                            onKeyDown={e => {
                              if (
                                header.column.getCanSort() &&
                                (e.key === 'Enter' || e.key === ' ')
                              ) {
                                header.column.getToggleSortingHandler()(e);
                              }
                            }}
                            onDragStart={isDraggable ? e => handleColumnDragStart(e, header.id) : undefined}
                            onDragEnd={handleColumnDragEnd}
                          >
                            {/* 1. Sort indicator */}
                            {header.column.getCanSort() && (
                              <Styled.SortIndicator
                                direction={header.column.getIsSorted()}
                              >
                                <span className='sort-arrow-up'>▲</span>
                                <span className='sort-arrow-down'>▼</span>
                              </Styled.SortIndicator>
                            )}

                            {/* 2. Column name */}
                            <span style={{ flex: 1 }}>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </span>

                            {/* 3. Filter icon */}
                            {header.column.getCanFilter() && (
                              <span onClick={e => e.stopPropagation()}>
                                <Popover
                                  content={
                                    <FilterPopoverContent column={header.column} />
                                  }
                                  trigger='click'
                                  visible={filterVisible[header.id] || false}
                                  onVisibleChange={visible =>
                                    setFilterVisible(prev => ({
                                      ...prev,
                                      [header.id]: visible
                                    }))
                                  }
                                  placement='bottomRight'
                                >
                                  <FilterOutlined
                                    style={{
                                      fontSize: 12,
                                      cursor: 'pointer',
                                      flexShrink: 0,
                                      color: header.column.getFilterValue()
                                        ? '#47a4ea'
                                        : 'rgba(0,0,0,0.25)'
                                    }}
                                  />
                                </Popover>
                              </span>
                            )}
                          </div>

                          {/* Resize handle */}
                          {header.column.getCanResize() && (
                            <Styled.ResizeHandle
                              onMouseDown={e => {
                                e.stopPropagation();
                                header.getResizeHandler()(e);
                              }}
                              onTouchStart={header.getResizeHandler()}
                              isResizing={header.column.getIsResizing()}
                              onClick={e => e.stopPropagation()}
                            />
                          )}
                        </Styled.DraggableHeader>
                      );
                    })}
                  </Styled.StyledTableHeaderRow>
                ))}
              </Styled.StyledTableHead>

              {/* Table Body */}
              <Styled.StyledTableBody role='rowgroup'>
                {table.getRowModel().rows.map(row => (
                  <Styled.StyledTableRow
                    key={row.id}
                    role='row'
                    selected={row.getIsSelected()}
                  >
                    {row.getVisibleCells().map(cell => (
                      <Styled.StyledTableCell key={cell.id} role='cell'>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Styled.StyledTableCell>
                    ))}
                  </Styled.StyledTableRow>
                ))}
              </Styled.StyledTableBody>
            </Styled.StyledTable>

            {/* Pagination Controls */}
            <Styled.PaginationContainer>
              <Styled.PaginationInfo>
                Showing{' '}
                {table.getState().pagination.pageIndex * ITEMS_PER_PAGE + 1} to{' '}
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) * ITEMS_PER_PAGE,
                  table.getFilteredRowModel().rows.length
                )}{' '}
                of {table.getFilteredRowModel().rows.length} results
                {(globalFilter ||
                  filteredData.length !== availableJobs?.length) &&
                  ` (filtered from ${availableJobs?.length || 0} total)`}
              </Styled.PaginationInfo>

              <Styled.PaginationControls>
                <Tooltip title='First Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<DoubleLeftOutlined />}
                    disabled={!table.getCanPreviousPage()}
                    onClick={() => table.setPageIndex(0)}
                    aria-label='First Page'
                  />
                </Tooltip>

                <Tooltip title='Previous Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<LeftOutlined />}
                    disabled={!table.getCanPreviousPage()}
                    onClick={() => table.previousPage()}
                    aria-label='Previous Page'
                  />
                </Tooltip>

                <span>
                  Page {table.getState().pagination.pageIndex + 1} of{' '}
                  {table.getPageCount() || 1}
                </span>

                <Tooltip title='Next Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<RightOutlined />}
                    disabled={!table.getCanNextPage()}
                    onClick={() => table.nextPage()}
                    aria-label='Next Page'
                  />
                </Tooltip>

                <Tooltip title='Last Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<DoubleRightOutlined />}
                    disabled={!table.getCanNextPage()}
                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    aria-label='Last Page'
                  />
                </Tooltip>
              </Styled.PaginationControls>
            </Styled.PaginationContainer>
          </>
        )}
      </Styled.TableContainer>
    </ThemeProvider>
  );
};

export default TanStackJobTable;
